/**
 * The six hulls (Design Bible §9.4, FS-10 §4.1). Deltas are quoted directly from the Bible and
 * MUST NOT be re-tuned here (FS-10 R04) — derived figures are recorded in FS-10 §5.2 and checked
 * against these values in `tests/hulls.test.ts`.
 */
import type { HullDef, HullId } from './types';

export const HULLS: { readonly [K in HullId]: HullDef } = {
  Vector: {
    id: 'Vector',
    displayName: 'Vector',
    identity: 'The reference hull. No delta, no cost — the baseline every other hull is measured against.',
    makeModifier: () => ({ accelScale: 1, hitboxScale: 1, beatAwardBonus: 1 }),
    hitPoints: 1,
    startingPowerup: null,
    powerupPolicy: 'all',
    scoreMultiplier: 1,
    unlock: { kind: 'always' },
    silhouette: 'vector',
  },
  Bassline: {
    id: 'Bassline',
    displayName: 'Bassline',
    identity: 'Two hit points at the cost of accel and hitbox — survives mistakes, makes more of them.',
    makeModifier: () => ({ accelScale: 0.8, hitboxScale: 1.15, beatAwardBonus: 1 }),
    hitPoints: 2,
    startingPowerup: null,
    powerupPolicy: 'all',
    scoreMultiplier: 1,
    unlock: { kind: 'run-stat', stat: 'hazardsCleared', threshold: 500, modes: ['any'] },
    silhouette: 'bassline',
  },
  Filament: {
    id: 'Filament',
    displayName: 'Filament',
    identity: 'A 0.30u hitbox and +30% accel for a player who does not get hit — one mistake ends it.',
    makeModifier: () => ({ accelScale: 1.3, hitboxScale: 0.3 / 0.45, beatAwardBonus: 1 }),
    hitPoints: 1,
    startingPowerup: null,
    powerupPolicy: 'no-shield',
    scoreMultiplier: 1,
    unlock: { kind: 'hit-free-sector', modes: ['sector-run', 'gauntlet'] },
    silhouette: 'filament',
  },
  Prism: {
    id: 'Prism',
    displayName: 'Prism',
    identity: 'Starts every run with a Harmonic Shield stored — flexible, and swappable for something better.',
    makeModifier: () => ({ accelScale: 1, hitboxScale: 1, beatAwardBonus: 1 }),
    hitPoints: 1,
    startingPowerup: 'Harmonic Shield',
    powerupPolicy: 'all',
    scoreMultiplier: 1,
    unlock: { kind: 'lifetime-stat', stat: 'overdrives', threshold: 10 },
    silhouette: 'prism',
  },
  Metronome: {
    id: 'Metronome',
    displayName: 'Metronome',
    identity: 'Beat-accurate steering awards +5 Resonance instead of +2 — rewards a specific skill.',
    makeModifier: () => ({ accelScale: 1, hitboxScale: 1, beatAwardBonus: 2.5 }),
    hitPoints: 1,
    startingPowerup: null,
    powerupPolicy: 'all',
    scoreMultiplier: 1,
    unlock: { kind: 'run-stat', stat: 'beatSteers', threshold: 200, modes: ['any'] },
    silhouette: 'metronome',
  },
  Null: {
    id: 'Null',
    displayName: 'Null',
    identity: 'No power-ups at all, but every point of score is doubled — the high-risk leaderboard hull.',
    makeModifier: () => ({ accelScale: 1, hitboxScale: 1, beatAwardBonus: 1 }),
    hitPoints: 1,
    startingPowerup: null,
    powerupPolicy: 'none',
    scoreMultiplier: 2,
    unlock: {
      kind: 'run-stat-all',
      conditions: [
        { stat: 'sectorIndexCleared', threshold: 5 },
        { stat: 'multiplierPeak', threshold: 8 },
      ],
      modes: ['sector-run', 'gauntlet', 'infinite'],
    },
    silhouette: 'null',
  },
};

export const ALL_HULLS: readonly HullId[] = ['Vector', 'Bassline', 'Filament', 'Prism', 'Metronome', 'Null'];
